import { EventBus, Events } from './index.js';

/**
 * AppState - Centralized state management
 * Observable state store with subscription support
 */

const AppState = {
  _state: {
    // View state
    currentView: 'landing', // 'landing' | 'course'

    // Navigation state
    currentModuleIndex: 0,
    currentStepIndex: 0,

    // UI state
    isLoading: false,
    isSidebarOpen: false,

    // User state (managed by AuthService)
    user: null,
    isAuthenticated: false
  },

  _listeners: new Map(),
  _initialized: false,

  /**
   * Initialize state from persisted data
   */
  init() {
    if (this._initialized) {
      return;
    }

    // Restore position from ProgressService if available
    if (typeof ProgressService !== 'undefined') {
      const position = ProgressService.getCurrentPosition();
      if (position.stepId) {
        this._restorePosition(position);
      }
    }

    // Set sidebar state based on viewport
    this._state.isSidebarOpen = window.innerWidth >= 1024;

    this._initialized = true;
    EventBus.emit(Events.APP_INITIALIZED, this._state);
  },

  /**
   * Restore navigation position from progress data
   * @private
   */
  _restorePosition(position) {
    if (!position.stepId || typeof COURSE_DATA === 'undefined') {
      return;
    }

    for (let mIndex = 0; mIndex < COURSE_DATA.modules.length; mIndex++) {
      const module = COURSE_DATA.modules[mIndex];
      const sIndex = module.steps.findIndex((s) => s.id === position.stepId);
      if (sIndex >= 0) {
        this._state.currentModuleIndex = mIndex;
        this._state.currentStepIndex = sIndex;
        break;
      }
    }
  },

  /**
   * Get a state value
   * @param {string} key - State key
   * @returns {*} State value
   */
  get(key) {
    return this._state[key];
  },

  /**
   * Set a state value
   * @param {string} key - State key
   * @param {*} value - New value
   */
  set(key, value) {
    const oldValue = this._state[key];
    if (oldValue === value) {
      return;
    }

    this._state[key] = value;
    this._notify(key, value, oldValue);
  },

  /**
   * Update multiple state values at once
   * @param {Object} updates - Object with key-value pairs
   */
  update(updates) {
    const changes = [];

    for (const [key, value] of Object.entries(updates)) {
      const oldValue = this._state[key];
      if (oldValue !== value) {
        this._state[key] = value;
        changes.push({ key, value, oldValue });
      }
    }

    // Notify all changes
    changes.forEach(({ key, value, oldValue }) => {
      this._notify(key, value, oldValue);
    });
  },

  /**
   * Subscribe to state changes
   * @param {string} key - State key to watch
   * @param {Function} callback - Callback function (newValue, oldValue)
   * @returns {Function} Unsubscribe function
   */
  subscribe(key, callback) {
    if (!this._listeners.has(key)) {
      this._listeners.set(key, new Set());
    }
    this._listeners.get(key).add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this._listeners.get(key);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  },

  /**
   * Notify subscribers of a state change
   * @private
   */
  _notify(key, newValue, oldValue) {
    const listeners = this._listeners.get(key);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(newValue, oldValue);
        } catch (error) {
          console.error(`Error in state listener for "${key}":`, error);
        }
      });
    }
  },

  // ==========================================
  // COMPUTED GETTERS
  // ==========================================

  /**
   * Get current module
   * @returns {Object|null} Current module
   */
  get currentModule() {
    if (typeof COURSE_DATA === 'undefined') {
      return null;
    }
    return COURSE_DATA.modules[this._state.currentModuleIndex];
  },

  /**
   * Get current step
   * @returns {Object|null} Current step
   */
  get currentStep() {
    const module = this.currentModule;
    if (!module) {
      return null;
    }
    return module.steps[this._state.currentStepIndex];
  },

  /**
   * Get total number of steps
   * @returns {number} Total steps
   */
  get totalSteps() {
    if (typeof COURSE_DATA === 'undefined') {
      return 0;
    }
    return COURSE_DATA.modules.reduce((total, mod) => total + mod.steps.length, 0);
  },

  // ==========================================
  // NAVIGATION HELPERS
  // ==========================================

  /**
   * Navigate to a specific step
   * @param {number} moduleIndex - Module index
   * @param {number} stepIndex - Step index
   */
  navigateTo(moduleIndex, stepIndex) {
    if (typeof COURSE_DATA === 'undefined') {
      return;
    }

    const module = COURSE_DATA.modules[moduleIndex];
    if (!module || stepIndex < 0 || stepIndex >= module.steps.length) {
      console.warn('Invalid navigation target:', moduleIndex, stepIndex);
      return;
    }

    const step = module.steps[stepIndex];

    this.update({
      currentModuleIndex: moduleIndex,
      currentStepIndex: stepIndex
    });

    // Persist position
    if (typeof ProgressService !== 'undefined') {
      ProgressService.setCurrentPosition(module.id, step.id);
    }

    EventBus.emit(Events.NAVIGATION_CHANGE, {
      moduleIndex,
      stepIndex,
      moduleId: module.id,
      stepId: step.id
    });
  },

  /**
   * Navigate to next step
   * @returns {boolean} Whether navigation was successful
   */
  nextStep() {
    if (typeof COURSE_DATA === 'undefined') {
      return false;
    }

    const currentModule = COURSE_DATA.modules[this._state.currentModuleIndex];

    if (this._state.currentStepIndex < currentModule.steps.length - 1) {
      // Next step in current module
      this.navigateTo(this._state.currentModuleIndex, this._state.currentStepIndex + 1);
      return true;
    } else if (this._state.currentModuleIndex < COURSE_DATA.modules.length - 1) {
      // First step in next module
      this.navigateTo(this._state.currentModuleIndex + 1, 0);
      return true;
    }

    return false;
  },

  /**
   * Navigate to previous step
   * @returns {boolean} Whether navigation was successful
   */
  prevStep() {
    if (typeof COURSE_DATA === 'undefined') {
      return false;
    }

    if (this._state.currentStepIndex > 0) {
      // Previous step in current module
      this.navigateTo(this._state.currentModuleIndex, this._state.currentStepIndex - 1);
      return true;
    } else if (this._state.currentModuleIndex > 0) {
      // Last step in previous module
      const prevModule = COURSE_DATA.modules[this._state.currentModuleIndex - 1];
      this.navigateTo(this._state.currentModuleIndex - 1, prevModule.steps.length - 1);
      return true;
    }

    return false;
  },

  /**
   * Check if at first step
   * @returns {boolean}
   */
  get isFirstStep() {
    return this._state.currentModuleIndex === 0 && this._state.currentStepIndex === 0;
  },

  /**
   * Check if at last step
   * @returns {boolean}
   */
  get isLastStep() {
    if (typeof COURSE_DATA === 'undefined') {
      return true;
    }

    const lastModuleIndex = COURSE_DATA.modules.length - 1;
    const lastModule = COURSE_DATA.modules[lastModuleIndex];
    const lastStepIndex = lastModule.steps.length - 1;

    return (
      this._state.currentModuleIndex === lastModuleIndex &&
      this._state.currentStepIndex === lastStepIndex
    );
  }
};

export { AppState };
